import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Briefcase, MapPin, Clock, ChevronRight, ArrowLeft, Loader2,
  CheckCircle2, ExternalLink, Search, Heart, Users, Award,
  Shield, Star, TrendingUp, Smile, Globe, Sparkles, Handshake,
} from 'lucide-react';

import { API, API_BASE as PUBLIC_BASE } from '../utils/apiBase';
const SL = { day_program: 'Explore Program', employment: 'Employment Services', in_home: 'In-Home & Community Supports' };
const EMP = { full_time: 'Full-Time', part_time: 'Part-Time', temp: 'Temporary', contract: 'Contract' };
const EMP_STYLE = {
  full_time: 'bg-[#E8F5E9] text-[#1B5E20]',
  part_time: 'bg-[#FFF3E0] text-[#E65100]',
  temp: 'bg-[#FFF8E1] text-[#F57F17]',
  contract: 'bg-[#E3F2FD] text-[#1565C0]',
};

const ICON_MAP = {
  heart: Heart, users: Users, award: Award, briefcase: Briefcase, shield: Shield,
  star: Star, 'trending-up': TrendingUp, smile: Smile, globe: Globe,
  sparkles: Sparkles, clock: Clock, handshake: Handshake,
};

const DEFAULT_BRANDING = {
  hero_title: 'Build a meaningful career.',
  hero_subtitle: 'Join our team supporting individuals with developmental disabilities across Pennsylvania. Explore Program, Employment Services, and In-Home Community Supports — your next step starts here.',
  footer_text: '© The Next Step Programs · An equal opportunity employer.',
  colors: { primary: '#6B3FA0', accent: '#9B7ED8', dark: '#2D1B4E' },
  value_props: [
    { id: 'vp1', icon: 'heart', title: 'Mission-driven', body: '' },
    { id: 'vp2', icon: 'users', title: 'Team-first culture', body: '' },
    { id: 'vp3', icon: 'award', title: 'Paid training', body: '' },
  ],
  has_hero_image: false,
  has_logo: false,
};

export default function CareersPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [postings, setPostings] = useState([]);
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [empFilter, setEmpFilter] = useState('all');

  const selectedId = params.get('apply');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [postRes, brandRes] = await Promise.all([
          axios.get(`${API}/public/postings`),
          axios.get(`${API}/public/branding`).catch(() => ({ data: DEFAULT_BRANDING })),
        ]);
        if (active) {
          setPostings(postRes.data || []);
          setBranding({ ...DEFAULT_BRANDING, ...(brandRes.data || {}), colors: { ...DEFAULT_BRANDING.colors, ...((brandRes.data || {}).colors || {}) } });
        }
      } catch {
        if (active) setPostings([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return postings.filter(p => {
      if (serviceFilter !== 'all' && p.service_line !== serviceFilter) return false;
      if (empFilter !== 'all' && p.employment_type !== empFilter) return false;
      if (q && !(p.title||'').toLowerCase().includes(q) && !(p.description||'').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [postings, search, serviceFilter, empFilter]);

  const selected = postings.find(p => p.id === selectedId);

  if (selectedId) {
    return <ApplyView postingId={selectedId} onBack={() => setParams({})} fallback={selected} branding={branding} />;
  }

  const c = branding.colors || DEFAULT_BRANDING.colors;
  const heroStyle = branding.has_hero_image
    ? { backgroundImage: `linear-gradient(135deg, ${c.dark}cc, ${c.primary}b3), url(${PUBLIC_BASE}/api/public/branding/hero-image)`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { backgroundImage: `linear-gradient(135deg, ${c.dark}, ${c.primary})` };
  const logoSrc = branding.has_logo ? `${PUBLIC_BASE}/api/public/branding/logo` : '/tns-logo.png';
  const visibleVps = (branding.value_props || []).filter(vp => (vp.title || '').trim() || (vp.body || '').trim());

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="careers-page" style={{ '--brand-primary': c.primary, '--brand-accent': c.accent, '--brand-dark': c.dark }}>
      {/* Header */}
      <header className="text-white" style={heroStyle}>
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
          <div className="flex items-center gap-2 mb-3">
            <img src={logoSrc} alt="Logo" className="h-10 w-10 rounded-md bg-white/10 p-1 object-contain" />
            <span className="text-sm font-semibold tracking-wide opacity-90">The Next Step Programs</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {branding.hero_title}
          </h1>
          <p className="mt-3 text-base sm:text-lg text-white/85 max-w-2xl whitespace-pre-line">
            {branding.hero_subtitle}
          </p>
          {visibleVps.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2 sm:gap-3 text-sm">
              {visibleVps.map(vp => {
                const Ic = ICON_MAP[vp.icon] || Heart;
                return (
                  <div key={vp.id} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/15" data-testid={`hero-vp-${vp.id}`}>
                    <Ic className="h-4 w-4"/>{vp.title || ''}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </header>

      {/* Value props (full cards if any have body text) */}
      {visibleVps.some(vp => (vp.body || '').trim()) && (
        <section className="max-w-5xl mx-auto px-4 sm:px-8 mt-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {visibleVps.map(vp => {
              const Ic = ICON_MAP[vp.icon] || Heart;
              return (
                <Card key={vp.id} className="border-[#DED8EB] shadow-sm" data-testid={`vp-card-${vp.id}`}>
                  <CardContent className="p-4">
                    <div className="h-9 w-9 rounded-full flex items-center justify-center mb-2" style={{ background: `${c.primary}1a`, color: c.primary }}>
                      <Ic className="h-4 w-4" />
                    </div>
                    <h3 className="text-sm font-bold" style={{ color: c.dark }}>{vp.title}</h3>
                    {vp.body && <p className="text-xs text-[#5A4D6E] mt-1">{vp.body}</p>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 -mt-6">
        <Card className="border-[#DED8EB] shadow-lg">
          <CardContent className="p-4 sm:p-5 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="flex-1 relative">
              <Search className="h-4 w-4 text-[#8E85A0] absolute left-3 top-1/2 -translate-y-1/2"/>
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by title or keyword..."
                className="border-[#DED8EB] pl-9"
                data-testid="careers-search"
              />
            </div>
            <select value={serviceFilter} onChange={e=>setServiceFilter(e.target.value)} className="border border-[#DED8EB] rounded-md px-3 py-2 text-sm bg-white" data-testid="careers-service-filter">
              <option value="all">All service lines</option>
              <option value="day_program">Explore Program</option>
              <option value="employment">Employment</option>
              <option value="in_home">In-Home Community Supports</option>
            </select>
            <select value={empFilter} onChange={e=>setEmpFilter(e.target.value)} className="border border-[#DED8EB] rounded-md px-3 py-2 text-sm bg-white" data-testid="careers-emp-filter">
              <option value="all">All types</option>
              <option value="full_time">Full-Time</option>
              <option value="part_time">Part-Time</option>
              <option value="temp">Temporary</option>
              <option value="contract">Contract</option>
            </select>
          </CardContent>
        </Card>
      </section>

      {/* Listings */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-xl sm:text-2xl font-bold" style={{ color: c.dark }}>Open positions</h2>
          <span className="text-sm text-[#8E85A0]">{filtered.length} listing{filtered.length===1?'':'s'}</span>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto" style={{ color: c.primary }}/></div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed border-[#DED8EB]"><CardContent className="p-10 text-center">
            <Briefcase className="h-12 w-12 mx-auto opacity-60 mb-2" style={{ color: c.accent }}/>
            <p className="text-base font-semibold" style={{ color: c.dark }}>No openings match your filters</p>
            <p className="text-xs text-[#8E85A0] mt-1">{postings.length === 0 ? 'Check back soon — new positions are posted regularly.' : 'Try clearing filters or broadening your search.'}</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(p => (
              <Card
                key={p.id}
                className="border-[#DED8EB] shadow-sm hover:shadow-md transition-all cursor-pointer group"
                style={{ '--hover-border': c.accent }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = c.accent}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#DED8EB'}
                onClick={() => navigate(`/careers?apply=${p.id}`)}
                data-testid={`careers-posting-${p.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold transition-colors" style={{ color: c.dark }}>{p.title}</h3>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {p.service_line && <Badge className="text-[10px] bg-[#F0EDF6] text-[#5A4D6E]"><MapPin className="h-2.5 w-2.5 mr-0.5"/>{SL[p.service_line]}</Badge>}
                        {p.employment_type && <Badge className={`text-[10px] ${EMP_STYLE[p.employment_type]||''}`}>{EMP[p.employment_type]}</Badge>}
                        {p.salary_range && <Badge className="text-[10px] bg-[#E8F5E9] text-[#1B5E20]">{p.salary_range}</Badge>}
                        {p.deadline && <span className="text-[11px] text-[#8E85A0] flex items-center gap-1"><Clock className="h-3 w-3"/>Apply by {p.deadline}</span>}
                      </div>
                      {p.description && (
                        <p className="text-sm text-[#5A4D6E] mt-2 line-clamp-2">{p.description}</p>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 mt-1 group-hover:translate-x-1 transition-transform" style={{ color: c.accent }}/>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Volunteer callout */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 pb-10">
        <div className="rounded-2xl p-6 sm:p-8 flex items-center justify-between flex-wrap gap-4"
             style={{ background: `linear-gradient(135deg, ${branding.colors?.primary || '#6B3FA0'}18, ${branding.colors?.accent || '#9B7ED8'}10)`, border: `1px solid ${branding.colors?.accent || '#9B7ED8'}40` }}>
          <div className="flex items-start gap-3 flex-1">
            <Heart className="h-6 w-6 shrink-0 mt-1" style={{ color: branding.colors?.primary || '#6B3FA0' }} />
            <div>
              <h3 className="text-xl font-bold text-[#2D1B4E]">Not looking for paid work?</h3>
              <p className="text-sm text-gray-600 mt-1">
                We welcome volunteers! Share your time, skills, or interests with our participants and community.
              </p>
            </div>
          </div>
          <Button onClick={() => navigate('/volunteer')} className="text-white"
                  style={{ background: branding.colors?.primary || '#6B3FA0' }}
                  data-testid="volunteer-cta">
            Volunteer with us →
          </Button>
        </div>
      </section>

      <footer className="max-w-5xl mx-auto px-4 sm:px-8 py-8 border-t border-[#DED8EB] text-center text-xs text-[#8E85A0]">
        {branding.footer_text}
      </footer>
    </div>
  );
}

function ApplyView({ postingId, onBack, fallback, branding }) {
  const [posting, setPosting] = useState(fallback || null);
  const [loading, setLoading] = useState(!fallback);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', resume_url: '', cover_letter: '',
    website: '', // honeypot
  });

  const c = (branding && branding.colors) || DEFAULT_BRANDING.colors;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/postings/${postingId}`);
        if (active) setPosting(data);
      } catch {
        if (active) toast.error('This posting is no longer available.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [postingId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/applications`, {
        posting_id: postingId,
        ...form,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]"><Loader2 className="h-7 w-7 animate-spin" style={{ color: c.primary }}/></div>;
  if (!posting) return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-4">
      <Card className="max-w-md w-full border-[#DED8EB]"><CardContent className="p-8 text-center">
        <p className="text-base font-semibold" style={{ color: c.dark }}>This posting is closed</p>
        <Button onClick={onBack} className="mt-4 text-white" style={{ background: c.primary }} data-testid="apply-back-closed">Back to all openings</Button>
      </CardContent></Card>
    </div>
  );

  const jd = posting.job_description;
  const headerStyle = { backgroundImage: `linear-gradient(135deg, ${c.dark}, ${c.primary})` };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="careers-apply-page">
      <header className="text-white" style={headerStyle}>
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6">
          <button onClick={onBack} className="text-sm text-white/80 hover:text-white inline-flex items-center gap-1 mb-3" data-testid="apply-back-btn">
            <ArrowLeft className="h-4 w-4"/>All openings
          </button>
          <h1 className="text-2xl sm:text-3xl font-extrabold">{posting.title}</h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap text-sm">
            {posting.service_line && <Badge className="bg-white/15 text-white border border-white/20">{SL[posting.service_line]}</Badge>}
            {posting.employment_type && <Badge className="bg-white/15 text-white border border-white/20">{EMP[posting.employment_type]}</Badge>}
            {posting.salary_range && <span className="text-white/90">{posting.salary_range}</span>}
            {posting.deadline && <span className="text-white/70 flex items-center gap-1"><Clock className="h-3.5 w-3.5"/>Closes {posting.deadline}</span>}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8 grid md:grid-cols-5 gap-6">
        {/* Posting details */}
        <section className="md:col-span-3 space-y-5">
          {posting.description && <Block title="About the role" body={posting.description} dark={c.dark}/>}
          {jd?.summary && <Block title="Overview" body={jd.summary} dark={c.dark}/>}
          {jd?.duties && <Block title="What you'll do" body={jd.duties} dark={c.dark}/>}
          {posting.requirements && <Block title="Requirements" body={posting.requirements} dark={c.dark}/>}
          {jd?.qualifications && <Block title="Qualifications" body={jd.qualifications} dark={c.dark}/>}
          {jd?.physical_reqs && <Block title="Physical requirements" body={jd.physical_reqs} dark={c.dark}/>}
          {jd?.benefits && <Block title="Benefits" body={jd.benefits} dark={c.dark}/>}
          {jd?.certifications && <Block title="Certifications" body={jd.certifications} dark={c.dark}/>}
        </section>

        {/* Apply form / success */}
        <aside className="md:col-span-2">
          <div className="md:sticky md:top-6">
            {submitted ? (
              <Card className="border-[#A5D6A7] bg-[#E8F5E9] shadow-md" data-testid="apply-success">
                <CardContent className="p-6 text-center">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-[#1B5E20] mb-2"/>
                  <h3 className="text-lg font-bold text-[#1B5E20]">Application received!</h3>
                  <p className="text-sm text-[#2E7D32] mt-1">Thanks for applying to <strong>{posting.title}</strong>. Our team will review your application and reach out if there's a fit.</p>
                  <Button onClick={onBack} className="mt-4 bg-[#1B5E20] hover:bg-[#2E7D32] text-white w-full" data-testid="apply-success-back">View other openings</Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-[#DED8EB] shadow-md" data-testid="apply-form-card">
                <CardContent className="p-5 space-y-3">
                  <h3 className="text-base font-bold text-[#2D1B4E]">Apply now</h3>
                  <form onSubmit={submit} className="space-y-3">
                    {/* Honeypot — hidden from real users */}
                    <input
                      type="text" name="website" tabIndex={-1} autoComplete="off"
                      value={form.website}
                      onChange={e=>setForm(f=>({...f, website: e.target.value}))}
                      style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
                      aria-hidden="true"
                    />
                    <div>
                      <Label htmlFor="apply-name" className="text-xs">Full name *</Label>
                      <Input id="apply-name" required value={form.name} onChange={e=>setForm(f=>({...f, name: e.target.value}))} className="border-[#DED8EB]" data-testid="apply-name"/>
                    </div>
                    <div>
                      <Label htmlFor="apply-email" className="text-xs">Email *</Label>
                      <Input id="apply-email" type="email" required value={form.email} onChange={e=>setForm(f=>({...f, email: e.target.value}))} className="border-[#DED8EB]" data-testid="apply-email"/>
                    </div>
                    <div>
                      <Label htmlFor="apply-phone" className="text-xs">Phone</Label>
                      <Input id="apply-phone" type="tel" value={form.phone} onChange={e=>setForm(f=>({...f, phone: e.target.value}))} className="border-[#DED8EB]" data-testid="apply-phone"/>
                    </div>
                    <div>
                      <Label htmlFor="apply-resume" className="text-xs">Resume / LinkedIn URL</Label>
                      <Input id="apply-resume" type="url" placeholder="https://linkedin.com/in/yourname" value={form.resume_url} onChange={e=>setForm(f=>({...f, resume_url: e.target.value}))} className="border-[#DED8EB]" data-testid="apply-resume"/>
                      <p className="text-[10px] text-[#8E85A0] mt-1 flex items-center gap-1"><ExternalLink className="h-2.5 w-2.5"/>Paste a public link (LinkedIn, Google Drive, etc.)</p>
                    </div>
                    <div>
                      <Label htmlFor="apply-cover" className="text-xs">Cover letter</Label>
                      <Textarea id="apply-cover" rows={4} value={form.cover_letter} onChange={e=>setForm(f=>({...f, cover_letter: e.target.value}))} className="border-[#DED8EB] text-sm" placeholder="Tell us why you're a great fit..." data-testid="apply-cover"/>
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full text-white" style={{ background: c.primary }} data-testid="apply-submit">
                      {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Sending...</> : 'Submit application'}
                    </Button>
                    <p className="text-[10px] text-[#8E85A0] text-center">By applying you agree to be contacted about this opportunity.</p>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
}

function Block({ title, body, dark }) {
  return (
    <div>
      <h2 className="text-sm font-bold uppercase tracking-wide mb-1.5" style={{ color: dark || '#2D1B4E' }}>{title}</h2>
      <p className="text-sm text-[#3D2E5C] whitespace-pre-wrap leading-relaxed">{body}</p>
    </div>
  );
}
